import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Briefcase, ArrowLeft, Plus, Building2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const CompaniesPage = ({ user }) => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    logo: '',
  });

  useEffect(() => {
    if (user) {
      fetchCompanies();
    }
  }, [user]);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/companies/my`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const createCompany = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/companies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Empresa creada exitosamente');
        setFormData({ name: '', description: '', website: '', logo: '' });
        setShowForm(false);
        fetchCompanies();
      }
    } catch (error) {
      console.error('Error creating company:', error);
      toast.error('Error al crear empresa');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-12 text-center">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-xl font-semibold mb-4">Inicia sesión para acceder</h3>
          <Button onClick={() => navigate('/auth')} data-testid="login-to-access-btn">
            Iniciar Sesión
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/dashboard" className="flex items-center space-x-3">
              <Briefcase className="w-7 h-7 text-red-600" />
              <span className="text-xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
                JobSuiza
              </span>
            </Link>

            <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Portal de Empresas</h1>
            <p className="text-lg text-slate-600">Gestiona tu empresa y publica ofertas de trabajo</p>
          </div>
          <Button onClick={() => setShowForm(!showForm)} data-testid="create-company-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Empresa
          </Button>
        </div>

        {/* Create Company Form */}
        {showForm && (
          <Card className="mb-8 shadow-lg" data-testid="company-form">
            <CardHeader>
              <CardTitle>Registrar Empresa</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={createCompany} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nombre de la Empresa</Label>
                  <Input
                    id="name"
                    placeholder="Ej: TechSwiss AG"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="company-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe tu empresa..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={4}
                    data-testid="company-description-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="website">Sitio Web (Opcional)</Label>
                    <Input
                      id="website"
                      type="url"
                      placeholder="https://example.com"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      data-testid="company-website-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="logo">URL del Logo (Opcional)</Label>
                    <Input
                      id="logo"
                      type="url"
                      placeholder="https://example.com/logo.png"
                      value={formData.logo}
                      onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                      data-testid="company-logo-input"
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button type="submit" data-testid="submit-company-btn">Registrar Empresa</Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)} data-testid="cancel-company-btn">
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Companies List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Cargando empresas...</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="companies-list">
            {companies.length === 0 ? (
              <Card className="p-12 text-center">
                <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-xl font-semibold mb-2">No tienes empresas registradas</h3>
                <p className="text-slate-600 mb-6">
                  Registra tu empresa para poder publicar ofertas de trabajo directamente en la plataforma
                </p>
                <Button onClick={() => setShowForm(true)} data-testid="create-first-company-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Registrar Primera Empresa
                </Button>
              </Card>
            ) : (
              companies.map((company) => (
                <Card key={company.id} className="hover:shadow-lg transition-all" data-testid={`company-card-${company.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start">
                      {company.logo ? (
                        <img src={company.logo} alt={company.name} className="w-16 h-16 rounded-lg mr-4 object-cover" />
                      ) : (
                        <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center mr-4">
                          <Building2 className="w-8 h-8 text-white" />
                        </div>
                      )}

                      <div className="flex-1">
                        <h3 className="text-2xl font-bold mb-2">{company.name}</h3>
                        <p className="text-slate-600 mb-3">{company.description}</p>

                        {company.website && (
                          <a
                            href={company.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-blue-600 hover:text-blue-700"
                          >
                            <Globe className="w-4 h-4 mr-1" />
                            Visitar sitio web
                          </a>
                        )}
                      </div>

                      <div className="ml-4">
                        <p className="text-sm text-slate-500 mb-2">Estado: Activa</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Info Section */}
        <Card className="mt-8 bg-red-50 border-red-200">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold mb-2 text-red-900">Próximamente</h3>
            <p className="text-red-800">
              Próximamente podrás publicar ofertas de trabajo directamente desde tu perfil de empresa. Por ahora, las
              empresas pueden registrarse y preparar su presencia en la plataforma.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CompaniesPage;